import * as React from 'react';
import styled from '@emotion/styled';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import BaseModal from '../components/modals/newModals/BaseModal';
import newtheme from '../newtheme';
import GoogleLoginButton from '../domaindriven/auth/components/GoogleLoginButton';
import { IState } from '../reducers';
import config from '../config';
import { getSelectedSubscriptionProduct } from '../domaindriven/subscribe/state/selectors';
import { getUser, signup, socialLogin } from '../domaindriven/auth/state/actions/user';
import { LOGIN_SUCCESS, SIGNUP_SUCCESS } from '../domaindriven/auth/state/constants/user';
import IconEye from '../assets/icons/IconEye';
import IconEyeCrossed from '../assets/icons/IconEyeCrossed';

interface ILandingPageValuePropProps {
    close: () => void;
    open: boolean;
}

const AuthModalNewLandingPageV2: React.FC<ILandingPageValuePropProps> = ({ close, open }) => {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const giftLink = useSelector((state: IState) => state.userSource.giftLink);
    const subscriptionProduct = useSelector((state: IState) => getSelectedSubscriptionProduct(state));

    // Form state
    const [name, setName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');

    const [showPassword, setShowPassword] = React.useState(false);

    // Validation error states
    const [isInvalidName, setIsInvalidName] = React.useState(false);
    const [isInvalidEmail, setIsInvalidEmail] = React.useState(false);
    const [isInvalidPassword, setIsInvalidPassword] = React.useState(false);

    const maybeRedirectAfterAuth = () => {
        if (location.pathname !== config.routes.home) {
            return;
        }

        if (subscriptionProduct && subscriptionProduct.metadata.homePage.redirectToPage) {
            history.push(subscriptionProduct.metadata.homePage.redirectToPage);
            return;
        }

        history.push(config.routes.latest);
    };

    const onSocialSign = () => {
        close();
        maybeRedirectAfterAuth();
    };
    const onGoogleSuccess = (response: any) => {
        if (response && response.profileObj) {
            dispatch(socialLogin({
                ssoId: response.googleId,
                email: response.profileObj.email,
                firstName: response.profileObj.givenName,
                methodTypeId: config.google.methodTypeId,
                giftLinkParam: giftLink && giftLink.giftLinkParam,
            }))
            // @ts-ignore
                .then((resp) => {
                    if (resp && resp.type === LOGIN_SUCCESS) {
                        dispatch(getUser());
                        onSocialSign();
                    }
                });
        }
    };

    const validateEmail = (emailValue: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(emailValue);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        let hasError = false;

        // Validate name
        if (!name || name.trim().length < 2) {
            setIsInvalidName(true);
            hasError = true;
        }

        // Validate email
        if (!email || !validateEmail(email.trim())) {
            setIsInvalidEmail(true);
            hasError = true;
        }

        // Validate password
        if (!password || password.trim().length < 6) {
            setIsInvalidPassword(true);
            hasError = true;
        }

        if (hasError) {
            return;
        }

        dispatch(signup({
            name: name.trim(),
            email: email.trim(),
            password: password.trim(),
        }),
        )
        // @ts-ignore
            .then((resp) => {
                if (resp && resp.type === SIGNUP_SUCCESS) {
                    dispatch(getUser());
                    onSocialSign();
                }
            });
    };

    return (
        <BaseModal
            modalId="ModalSignup"
            isOpen={open}
            onClose={close}
            title="Signup"
            height="EXTRA_LARGE"
            scrollable={false}
            fullScreenMobile
            width="535px">

            <CloseButton onClick={close}>
                <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.625 45.375L45.375 20.625" stroke="black" strokeWidth="2.475" strokeLinecap="round" />
                    <path d="M20.625 20.625L45.375 45.375" stroke="black" strokeWidth="2.475" strokeLinecap="round" />
                </svg>
            </CloseButton>

            <Main>
                <TextContainer>
                    <h2>Sign Up</h2>
                    <LoginText>Already have an account? <a href={config.routes.login}>Log In.</a></LoginText>
                </TextContainer>

                <GoogleLoginButtonContainer><GoogleLoginButton
                    onSuccess={onGoogleSuccess}
                    onFailure={() => { console.log('Google login failed'); }}
                    text="Continue with Google"
                    orientation="column"
                />
                </GoogleLoginButtonContainer>

                <OrSeparator>
                    <div />
                    <p>Or</p>
                    <div />
                </OrSeparator>

                <SignUpForm onSubmit={handleSubmit}>
                    <InputWrapper>
                        <div>
                            <svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.3203 12.2566C12.0862 12.2566 11.5329 12.9375 9.53271 12.9375C7.48995 12.9375 6.9367 12.2566 5.70253 12.2566C2.55327 12.2566 -0.000180244 14.8526 -0.000180244 18.0018V19.7467C-0.000180244 20.8958 0.893529 21.7895 2.04258 21.7895H17.0228C18.1293 21.7895 19.0656 20.8958 19.0656 19.7467V18.0018C19.0656 14.8526 16.4696 12.2566 13.3203 12.2566ZM17.7038 19.7467C17.7038 20.1297 17.3633 20.4276 17.0228 20.4276H2.04258C1.65956 20.4276 1.36166 20.1297 1.36166 19.7467V18.0018C1.36166 15.5761 3.27675 13.6184 5.70253 13.6184C6.55369 13.6184 7.36228 14.2993 9.53271 14.2993C11.6606 14.2993 12.5117 13.6184 13.3203 13.6184C15.7461 13.6184 17.7038 15.5761 17.7038 18.0018V19.7467ZM9.53271 10.8947C12.5117 10.8947 14.9801 8.46895 14.9801 5.44737C14.9801 2.46834 12.5117 -1.90735e-06 9.53271 -1.90735e-06C6.51113 -1.90735e-06 4.08535 2.46834 4.08535 5.44737C4.08535 8.46895 6.51113 10.8947 9.53271 10.8947ZM9.53271 1.36184C11.7457 1.36184 13.6182 3.23437 13.6182 5.44737C13.6182 7.70292 11.7457 9.53289 9.53271 9.53289C7.27716 9.53289 5.44719 7.70292 5.44719 5.44737C5.44719 3.23437 7.27716 1.36184 9.53271 1.36184Z" fill="#039BE5" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onFocus={() => setIsInvalidName(false)}
                                className={isInvalidName ? 'error' : ''}
                            />
                        </div>
                        {isInvalidName && <ErrorMessage>Please enter a valid name (at least 2 characters)</ErrorMessage>}
                    </InputWrapper>
                    <InputWrapper>
                        <div>
                            <svg width="22" height="17" viewBox="0 0 22 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19.7465 0H2.04256C0.893503 0 -0.000205994 0.936266 -0.000205994 2.04276V14.2993C-0.000205994 15.4484 0.893503 16.3421 2.04256 16.3421H19.7465C20.853 16.3421 21.7893 15.4484 21.7893 14.2993V2.04276C21.7893 0.936266 20.853 0 19.7465 0ZM2.04256 1.36184H19.7465C20.087 1.36184 20.4274 1.7023 20.4274 2.04276V3.83018C19.4912 4.59622 18.1293 5.70271 14.0012 8.97965C13.2778 9.57545 11.8734 10.9373 10.8945 10.8947C9.87315 10.9373 8.46875 9.57545 7.74527 8.97965C3.61719 5.70271 2.25535 4.59622 1.36164 3.83018V2.04276C1.36164 1.7023 1.65954 1.36184 2.04256 1.36184ZM19.7465 14.9803H2.04256C1.65954 14.9803 1.36164 14.6824 1.36164 14.2993V5.57504C2.2979 6.38364 3.82998 7.6178 6.89412 10.0436C7.78783 10.7671 9.3199 12.2991 10.8945 12.2566C12.4266 12.2991 13.9587 10.7671 14.8524 10.0436C17.9165 7.6178 19.4486 6.38364 20.4274 5.57504V14.2993C20.4274 14.6824 20.087 14.9803 19.7465 14.9803Z" fill="#039BE5" />
                            </svg>
                            <input
                                type="email"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onFocus={() => setIsInvalidEmail(false)}
                                className={isInvalidEmail ? 'error' : ''}
                            />
                        </div>
                        {isInvalidEmail && <ErrorMessage>Please enter a valid email address</ErrorMessage>}
                    </InputWrapper>
                    <InputWrapper>
                        <PasswordInputContainer>
                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14.2991 1.36184C17.6612 1.36184 20.4274 4.12808 20.4274 7.49013C20.4274 10.8947 17.6612 13.6184 14.2991 13.6184C13.4905 13.6184 12.6819 13.4907 12.001 13.1928L10.2136 14.9803H8.17085V17.7039H5.44716V20.4276H1.36164V17.023L8.59642 9.78824C8.29852 9.10732 8.17085 8.29872 8.17085 7.49013C8.17085 4.12808 10.8945 1.36184 14.2991 1.36184ZM14.2991 -1.90735e-06C10.1285 -1.90735e-06 6.809 3.36205 6.809 7.49013C6.809 8.17105 6.85156 8.80941 7.02179 9.40522L0.297697 16.1719C0.127467 16.3421 -0.000205994 16.6826 -0.000205994 16.8954V20.7681C-0.000205994 21.3639 0.42537 21.7895 1.02118 21.7895H5.78762C6.34087 21.7895 6.809 21.3639 6.809 20.7681V19.0658H8.51131C9.06456 19.0658 9.53269 18.6402 9.53269 18.0444V16.3421H10.3413C10.5966 16.3421 10.852 16.257 11.0648 16.0442L12.384 14.7675C12.9799 14.9377 13.6182 14.9803 14.2991 14.9803C18.4272 14.9803 21.7893 11.6608 21.7893 7.49013C21.7893 3.36205 18.4272 -1.90735e-06 14.2991 -1.90735e-06ZM16.3419 4.59622C16.81 4.59622 17.1931 4.97923 17.1931 5.44737C17.1931 5.95806 16.81 6.29852 16.3419 6.29852C15.8312 6.29852 15.4907 5.95806 15.4907 5.44737C15.4907 4.97923 15.8312 4.59622 16.3419 4.59622ZM16.3419 3.4046C15.1928 3.4046 14.2991 4.34087 14.2991 5.44737C14.2991 6.59642 15.1928 7.49013 16.3419 7.49013C17.4484 7.49013 18.3847 6.59642 18.3847 5.44737C18.3847 4.34087 17.4484 3.4046 16.3419 3.4046Z" fill="#039BE5" />
                            </svg>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onFocus={() => setIsInvalidPassword(false)}
                                className={isInvalidPassword ? 'error' : ''}
                            />
                            <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <IconEyeCrossed /> : <IconEye />}
                            </PasswordToggle>
                        </PasswordInputContainer>
                        {isInvalidPassword && <ErrorMessage>Password must be at least 6 characters</ErrorMessage>}
                    </InputWrapper>
                </SignUpForm>

                <CtaButtonValueProp type="submit" form="signup-form" onClick={handleSubmit}>
                    Sign Up
                </CtaButtonValueProp>
                <TermsAndPrivacyText>By creating an account, you agree to our <a href={config.routes.terms} target="_blank" rel="noreferrer">Terms of Service</a> & <a href={config.routes.privacy} target="_blank" rel="noreferrer">Privacy Policy</a>.</TermsAndPrivacyText>
            </Main>
        </BaseModal>
    );
};

const CloseButton = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;

    padding: 0;
    margin: 0;
    border: none;
    background: none;
    cursor: pointer;
    outline: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;

    svg {
        width: 40px
    }
`;

const Main = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    background: #ffffff;
    padding: 70px;

    ${newtheme.cssBreakpoints.small}: {
        padding: 60px 40px;
    }
`;

const TextContainer = styled.div`
    align-self: flex-start;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 10px;

    a, p, h2 {
        padding: 0;
        margin: 0;
    }

    h2 {
        font-size: 36px;
        font-weight: 500;
        text-align: left;
        line-height: 1.4;
        color: #000000;
    }

     ${newtheme.cssBreakpoints.small}: {
        h2 {
        font-size: 34px;
        }
     }
`;

const LoginText = styled.div`
        font-size: 18px;
        text-align: left;
        line-height: 1.4;
        color: #000000;


    a {
        cursor: pointer;
        text-decoration: none;
        color: ${newtheme.colors.primaryBlue} !important;
    }


     ${newtheme.cssBreakpoints.small}: {
        font-size: 16px;
     }
`;

export const GoogleLoginButtonContainer = styled.div`
    flex-shrink: 0;
    width: 100%;
    display: flex;
    flex-wrap: wrap;

    margin-top: 48px;
    margin-bottom: 40px;
`;

export const OrSeparator = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 10px;

    p {
        font-size: 18px;
        text-align: center;
        color: #000000;

        margin: 0;
        padding: 0;

        margin-top: -2px;
    }

    div {
        width: 100%;
        height: 1px;
        background-color: #C4C4C4;
    }

      ${newtheme.cssBreakpoints.small}: {
        p {
        font-size: 16px;
        }
      }
`;

export const SignUpForm = styled.form`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 20px;

    margin-top: 30px;
    margin-bottom: 30px;

    & > div > div {
        position: relative;
        width: 100%;
    }

    svg {
        position: absolute;
        top: 50%;
        left: 20px;
        transform: translateY(-50%);
        z-index: 1;
    }

    input {
        width: 100%;
        height: 56px;
        border: 1px solid #C4C4C4;

        border-radius: 11px;
        padding: 0 20px;
        padding-left: 55px;
        font-size: 16px;
        color: #000000;

        ::placeholder {
            color: #CBCBCB;
        }

        &.error {
            border-color: #E53935;
        }

        &:focus {
            outline: none;
            border-color: ${newtheme.colors.primaryBlue};
        }
    }

      ${newtheme.cssBreakpoints.small} {

            input {
                font-size: 15px;
                padding: 0 16px;
                height: 52px;
                padding-left: 48px;
            }

            svg {
                width: 18px;
                left: 17px;
            }

    }
`;

const InputWrapper = styled.div`
    width: 100%;
`;

const PasswordInputContainer = styled.div`
    position: relative;
    width: 100%;

    input {
        padding-right: 50px !important;
    }
`;

const PasswordToggle = styled.button`
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-48%);
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;

    svg {
        position: static !important;
        transform: none !important;
    }
`;

const ErrorMessage = styled.span`
    display: block;
    color: #E53935;
    font-size: 13px;
    margin-top: 6px;
    padding-left: 4px;
`;

export const CtaButtonValueProp = styled.button`

    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    background-color: ${newtheme.colors.primaryBlue};
    color: ${newtheme.darkmodeColors.white2};
    border: none;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 500;
    padding: 14px 63px;
    transition: background-color 0.2s ease;
    margin: 0 auto;

    &:hover {
        background-color: #0288d1;
        color: rgba(255, 255, 255, 0.8);
        text-decoration: none;
    }

    ${newtheme.cssBreakpoints.small}: {
            font-size: 15px;
            padding: 12px 63px;
            border-radius: 10px;

    }

`;

export const TermsAndPrivacyText = styled.div`
color: #737373;
text-align: center;
font-size: 14px;
line-height: 1.4;
max-width: 325px;
margin-top: 15px;

 a {
        cursor: pointer;
        color: ${newtheme.colors.primaryBlue} !important;
    }

      ${newtheme.cssBreakpoints.small}: {
            font-size: 12px;

`;

export default AuthModalNewLandingPageV2;
